import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { VIZ, axisStyle, crosshairPlugin, withAlpha } from '../../Visualization/chartTheme';

const STATUS_CRITICAL = '#d03b3b';

const fmtDate = (value) =>
  new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

const fmtDateTime = (value) =>
  new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const isOut = (point, series) =>
  (series.normalLow !== null && series.normalLow !== undefined && point.v < series.normalLow) ||
  (series.normalHigh !== null && series.normalHigh !== undefined && point.v > series.normalHigh);

/**
 * Shades the clinically normal band behind the line, so a reader judges a
 * reading against the range rather than against the other readings.
 */
const normalBandPlugin = {
  id: 'normalBand',
  beforeDatasetsDraw(chart, args, opts) {
    const { low, high } = opts || {};
    if (low === null && high === null) return;
    if (low === undefined && high === undefined) return;

    const { ctx, chartArea, scales } = chart;
    const y = scales.y;
    const top = y.getPixelForValue(high ?? y.max);
    const bottom = y.getPixelForValue(low ?? y.min);

    ctx.save();
    ctx.fillStyle = 'rgba(12, 163, 12, 0.07)';
    ctx.fillRect(
      chartArea.left,
      Math.max(top, chartArea.top),
      chartArea.right - chartArea.left,
      Math.min(bottom, chartArea.bottom) - Math.max(top, chartArea.top)
    );
    ctx.restore();
  },
};

/**
 * One numeric vital over time. Deliberately one chart per field — blood
 * pressure in mmHg and pulse in bpm on one plot would need a second y-axis,
 * which invents correlations that are not in the data.
 */
function VitalSeriesChart({ series }) {
  const { points, summary } = series;

  const data = useMemo(
    () => ({
      labels: points.map((p) => fmtDate(p.t)),
      datasets: [
        {
          label: series.label,
          data: points.map((p) => p.v),
          borderColor: VIZ.sequential,
          backgroundColor: withAlpha(VIZ.sequential, 0.1),
          fill: true,
          borderWidth: 2,
          tension: 0.25,
          // A reading outside the normal band is re-coloured AND called out in
          // the header count, so the colour is never the only signal.
          pointRadius: points.map((p) => (isOut(p, series) ? 5 : 4)),
          pointHoverRadius: 7,
          pointBackgroundColor: points.map((p) => (isOut(p, series) ? STATUS_CRITICAL : VIZ.sequential)),
          pointBorderColor: VIZ.surface,
          pointBorderWidth: 2,
          pointHitRadius: 24,
        },
      ],
    }),
    [points, series]
  );

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: axisStyle(false),
        y: { ...axisStyle(true), ticks: { ...axisStyle().ticks, maxTicksLimit: 5 } },
      },
      plugins: {
        // One series — the card heading names it, so a legend box adds nothing.
        legend: { display: false },
        normalBand: { low: series.normalLow, high: series.normalHigh },
        tooltip: {
          backgroundColor: '#141413',
          padding: 10,
          cornerRadius: 8,
          titleColor: '#f5f4f1',
          bodyColor: '#ffffff',
          displayColors: false,
          callbacks: {
            title: (items) => fmtDateTime(points[items[0].dataIndex]?.t),
            label: (ctx) => {
              const point = points[ctx.dataIndex];
              const base = `${ctx.parsed.y}${series.unit ? ` ${series.unit}` : ''}`;
              const flag = isOut(point, series) ? '  ·  outside normal' : '';
              const who = point?.source === 'portal' ? '  ·  patient entered' : '';
              return `${base}${flag}${who}`;
            },
          },
        },
      },
    }),
    [points, series]
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-800">
          {series.label}
          {series.unit && <span className="ml-1 font-normal text-slate-500">({series.unit})</span>}
        </h4>
        {summary && (
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium ${
              summary.outOfRange > 0 ? 'text-rose-600' : 'text-emerald-600'
            }`}
          >
            {summary.outOfRange > 0 ? <FaExclamationTriangle /> : <FaCheckCircle />}
            {summary.outOfRange > 0
              ? `${summary.outOfRange} of ${summary.count} outside normal`
              : 'All readings normal'}
          </span>
        )}
      </div>

      {summary && (
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
          <span>
            Latest <strong className="text-slate-900">{summary.latest}</strong>
          </span>
          <span>
            Average <strong className="text-slate-900">{summary.average}</strong>
          </span>
          <span>
            Range <strong className="text-slate-900">{summary.min}–{summary.max}</strong>
          </span>
          <span>
            Readings <strong className="text-slate-900">{summary.count}</strong>
          </span>
        </div>
      )}

      <div className="mt-3 h-52">
        {points.length > 1 ? (
          <Line data={data} options={options} plugins={[crosshairPlugin, normalBandPlugin]} />
        ) : (
          <div className="flex h-full items-center justify-center rounded-lg bg-slate-50 text-xs text-slate-500">
            {points.length === 1
              ? 'One reading so far — a trend appears from the second onwards.'
              : 'No readings recorded for this field yet.'}
          </div>
        )}
      </div>
    </div>
  );
}

export default function VitalsCharts({ series = [] }) {
  const withData = series.filter((s) => s.points?.length);

  if (!withData.length) {
    return (
      <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
        No numeric readings yet. Graphs appear automatically once readings are recorded.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {withData.map((s) => (
        <VitalSeriesChart key={s.key} series={s} />
      ))}
    </div>
  );
}
